import React, { useState, useEffect } from 'react';
import { Clock, User, Calendar, Eye, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const ArticlePreviewBoard = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/articles`);
        const data = await response.json();
        setArticles(data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #1a1a2e 100%)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #1a1a2e 100%)' }}>
      {/* Header */}
      <div className="pt-12 pb-8 px-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>
          
          <div className="text-center">
            <p className="text-yellow-400 text-sm uppercase tracking-widest mb-2">Article Preview Board</p>
            <h1 className="text-4xl font-light text-white mb-2">INSPIYAH Journey</h1>
            <p className="text-gray-400 text-sm">Review layout before publishing to live journey page ({articles.length} articles)</p>
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <div 
              key={article.id || article.slug}
              className="group bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-yellow-400/30 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-400/5"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={article.image || article.image_url || 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800&h=400&fit=crop'}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                
                {/* Status Badge */}
                <div className="absolute top-3 right-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    article.published 
                      ? 'bg-green-500/90 text-white' 
                      : 'bg-yellow-500/90 text-black'
                  }`}>
                    {article.published ? 'Published' : 'Draft'}
                  </span>
                </div>

                {/* Category */}
                <div className="absolute bottom-3 left-3">
                  <span className="px-2.5 py-1 bg-purple-600/80 backdrop-blur-sm text-white text-xs rounded-full">
                    {article.category || 'Spirituality'}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-white font-semibold text-lg leading-tight mb-2 line-clamp-2 group-hover:text-yellow-400 transition-colors">
                  {article.title}
                </h3>
                
                <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                  {article.excerpt || 'YAHFFIRMATIONS\u00a9 BY KOTF888\u00a9'}
                </p>

                {/* Meta */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-3">
                    <span className="flex items-center space-x-1">
                      <User size={12} />
                      <span>{article.author || 'KOTF'}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Clock size={12} />
                      <span>{article.readTime || article.read_time || '5 min'}</span>
                    </span>
                  </div>
                  <span className="flex items-center space-x-1">
                    <Calendar size={12} />
                    <span>{article.date || 'Recent'}</span>
                  </span>
                </div>

                {/* View Button */}
                <a
                  href={`/article/${article.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-purple-600/20 to-orange-600/20 border border-purple-500/30 text-purple-300 rounded-lg hover:from-purple-600/40 hover:to-orange-600/40 hover:text-white transition-all text-sm"
                >
                  <Eye size={16} />
                  <span>View Article</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ArticlePreviewBoard;
